import {
  AnalyticsEventName,
  AnalyticsEventProps,
} from 'services/api/apis/analytics';

export type { AnalyticsEventName, AnalyticsEventProps };

/**
 * Discriminated union of every analytics event the web app fires.
 *
 * Adding a new event:
 *  1. Add the name to `ANALYTICS_EVENT_NAMES` in
 *     `services/web/services/api/apis/analytics/dto/track-events.dto.ts`.
 *  2. Add the matching backend whitelist entry in
 *     `services/api/src/app/analytics/dto/track-events.request.dto.ts`.
 *  3. Add a variant below.
 *
 * Tracked in CAM-784.
 */
export type AnalyticsEvent =
  | { name: 'org_tab_viewed' }
  | {
      name: 'my_org_card_clicked';
      props: {
        role: 'leader' | 'admin' | 'member';
        action: 'view' | 'manage';
      };
    }
  | {
      name: 'nya_item_clicked';
      props: {
        type:
          | 'membership_request'
          | 'leader_access_request'
          | 'event_needs_approval'
          | 'rejected_event';
      };
    }
  | { name: 'nya_admin_link_clicked' }
  | { name: 'directory_search_used' }
  | { name: 'recommended_events_row_clicked' }
  | { name: 'org_followed'; props: { org_id: string } }
  | { name: 'org_unfollowed'; props: { org_id: string } }
  | {
      name: 'admin_users_searched';
      props: {
        query_length: number;
        result_count: number;
        had_results: boolean;
      };
    }
  | {
      name: 'admin_users_filter_applied';
      props: { role: string; result_count: number };
    }
  | {
      name: 'admin_users_filter_cleared';
      props: { previous_role: string };
    }
  | {
      name: 'admin_users_exported';
      props: {
        role_filter: string;
        had_search: boolean;
        row_count: number;
        format: string;
      };
    }
  | {
      name: 'admin_users_export_failed';
      props: { reason: string; row_count: number };
    }
  | {
      name: 'admin_user_row_action_clicked';
      props: {
        action: 'transcript' | 'edit_role';
        row_position: number;
        page_number: number;
      };
    }
  | {
      name: 'admin_role_modal_opened';
      props: { roles_held: string; role_count: number };
    }
  | {
      name: 'admin_role_modal_saved';
      props: { roles_before: string; roles_after: string; changed_count: number };
    }
  | {
      name: 'admin_role_modal_dismissed';
      props: { had_changes: boolean };
    }
  | {
      name: 'admin_users_page_changed';
      props: {
        from_page: number;
        to_page: number;
        direction: 'next' | 'previous';
      };
    }
  | {
      name: 'admin_transcript_opened';
      props: { user_id: string };
    }
  | {
      name: 'admin_transcript_exported';
      props: { user_id: string; row_count: number };
    }
  | {
      name: 'admin_role_changed';
      props: {
        subject_user_id: string;
        roles_before: string;
        roles_after: string;
        changed_count: number;
      };
    }
  | {
      name: 'schedule_viewed';
      props: {
        view: 'list' | 'day' | 'week' | 'month';
        entry: 'load' | 'switch' | 'back';
        event_count: number;
        filters_active: boolean;
        viewport: 'desktop' | 'mobile';
        role: 'exec_admin' | 'facility_manager';
      };
    }
  | {
      name: 'schedule_event_opened';
      props: {
        source: 'list_review' | 'week_block' | 'day_block' | 'month_chip';
        status: string;
        had_conflict: boolean;
        viewport: 'desktop' | 'mobile';
        role: 'exec_admin' | 'facility_manager';
      };
    }
  | {
      name: 'schedule_day_opened';
      props: {
        source: 'month_cell';
        event_count: number;
        viewport: 'desktop' | 'mobile';
        role: 'exec_admin' | 'facility_manager';
      };
    }
  | {
      name: 'schedule_filtered';
      props: {
        type: 'filters' | 'search';
        source?: 'modal' | 'date_field' | 'chip_remove' | 'chip_clear_all';
        groups_used: number;
        values_count?: number;
        result_count: number;
        query_length?: number;
        viewport: 'desktop' | 'mobile';
        role: 'exec_admin' | 'facility_manager';
      };
    }
  | {
      name: 'schedule_exported';
      props: {
        row_count: number;
        view: 'list' | 'day' | 'week' | 'month';
        filters_active: boolean;
        viewport: 'desktop' | 'mobile';
        role: 'exec_admin' | 'facility_manager';
      };
    }
  | {
      name: 'connected_dashboard_viewed';
      props: { layout: 'desktop' | 'mobile' };
    }
  | {
      name: 'today_event_card_impression';
      props: {
        event_id: string;
        position: number;
        event_state: 'now' | 'upcoming_today';
      };
    }
  | {
      name: 'event_check_in_started';
      props: {
        event_id: string;
        role: 'attendee' | 'organizer';
        source: 'happening_today' | 'upcoming';
      };
    }
  | {
      name: 'event_rsvp_clicked';
      props: { event_id: string; source: 'upcoming' };
    }
  | {
      name: 'see_all_events_clicked';
      props: { source: 'upcoming' };
    };

/**
 * Set of event names that should fire at most once per session, regardless of
 * how many times `track()` is called. Keeps the directory-search KPI clean.
 */
export const ONCE_PER_SESSION: ReadonlySet<AnalyticsEventName> = new Set<AnalyticsEventName>([
  'directory_search_used',
]);
